import React, { FormEvent, ChangeEvent } from "react";
import { DateTime } from "luxon";

import withTitle from "../withTitle";
import ValidateInput from "../ValidateInput";
import { V2 } from "../../Api";
import { IAuthProps } from "../AuthContext";
import withAuth from "../withAuth";
import { formatName, validateEmail } from "../../Utilities";

interface IMissedTelemedsViewState {
    isLoading: boolean | string;
    isLookingupPatient: boolean;
    hasChanged: boolean;

    emails: any[];
    templates: any[];

    chartNumber: string;
    name: string;
    emailAddress: string;
    fromAddress: string;
    template: string;
    appointmentDate: string;
    appointmentTime: string;
}

class MissedTelemedsView extends React.Component<IAuthProps, IMissedTelemedsViewState> {
    private _inputRefs: ValidateInput[] = [];

    constructor(props: Readonly<IAuthProps>) {
        super(props);

        const state: IMissedTelemedsViewState = {
            isLoading: false,
            isLookingupPatient: false,
            hasChanged: false,

            emails: [],
            templates: [],

            chartNumber: "",
            name: "",
            emailAddress: "",
            fromAddress: "",
            template: "",
            appointmentDate: DateTime.local().toISODate(), // Default to today
            appointmentTime: ""
        };

        this.state = state;

        this.submitForm = this.submitForm.bind(this);
        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.lookupPatient = this.lookupPatient.bind(this);
    }

    public async componentDidMount() {
        try {
            const [allEmails, templates] = await Promise.all([
                V2.get<any>("locations/emails?type=telemed"),
                V2.get<any>("appointments/missed-telemed/templates"),
            ]);
            this.setState({
                emails: allEmails.emailAddresses,
                templates: templates.templates
            });

            if (this.props.authContext.detectedLocationId) {
                const email = await V2.get<any>(`locations/${this.props.authContext.detectedLocationId}/email?type=telemed`);

                this.setState({
                    fromAddress: email.emailAddress
                });
            }
        }
        catch (e) {
        }
    }

    public render() {
        return (
            <div className="viewer-root">
                <p>Enter the patient's information below, and the system will send them an email about their missed appointment.</p>
                <form className="input-form" onSubmit={this.submitForm}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h2>Patient Information</h2>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-chart" className="required">Chart Number</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-chart" name="chartNumber" value={this.state.chartNumber} onChange={this.formInputChanged} onBlur={this.lookupPatient} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name" className="required">Patient Name</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name" className={this.state.isLookingupPatient ? "input-waiting" : ""} name="name" value={this.state.name} onChange={this.formInputChanged} readOnly={this.state.isLookingupPatient} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-email" className="required">Email Address</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-email" className={this.state.isLookingupPatient ? "input-waiting" : ""} name="emailAddress" value={this.state.emailAddress} onChange={this.formInputChanged} readOnly={this.state.isLookingupPatient} autoComplete="off_please_chrome" isRequired={true} validator={validateEmail} />
                                </div>
                            </section>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-appt-date" className="required">Appointment Date</label>
                                    <ValidateInput type="date" ref={this.receiveValidationRef} id="frm-appt-date" name="appointmentDate" value={this.state.appointmentDate} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-appt-time" className="required">Appointment Time</label>
                                    <ValidateInput type="time" ref={this.receiveValidationRef} id="frm-appt-time" name="appointmentTime" value={this.state.appointmentTime} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>
                        </section>
                        <section className="info-block">
                            <header>
                                <h2>Email Information</h2>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-from-address" className="required">Send From Email Address</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-from-address" name="fromAddress" value={this.state.fromAddress} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                        <option value="">Select one</option>
                                        {this.state.emails.map(a => <option key={a.emailAddress} value={a.emailAddress}>{a.name} ({a.emailAddress})</option>)}
                                    </ValidateInput>
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-template" className="required">Message Template</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-template" name="template" value={this.state.template} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                        <option value="">Select one</option>
                                        {this.state.templates.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                    </ValidateInput>
                                </div>
                            </section>
                        </section>
                    </div>
                    &nbsp;
                    <div className="action-panel-dialog">
                        <span className="spring" />
                        <button type="submit" className="button" disabled={this.state.isLoading === true}>Send Message</button>
                    </div>
                    {this.state.isLoading === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isLoading === "string"
                            ? <div className="error-text">{this.state.isLoading}</div>
                            : null
                    }
                </form>
            </div>
        );
    }

    private receiveValidationRef(ref: ValidateInput) {
        this._inputRefs.push(ref);
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            hasChanged: true,
            [name]: value
        } as any);
    }

    private async submitForm(e: FormEvent) {
        e.preventDefault();

        // Validate inputs
        let isValid = true;
        for (const input of this._inputRefs) {
            isValid = input.validate() && isValid;
        }

        if (!isValid) {
            return;
        }
        try {
            this.setState({
                isLoading: true
            });

            const data = {
                chart: this.state.chartNumber,
                name: this.state.name,
                emailAddress: this.state.emailAddress,
                templateId: this.state.template,
                fromAddress: this.state.fromAddress,
                appointmentDate: this.state.appointmentDate,
                appointmentTime: this.state.appointmentTime
            };

            await V2.post("appointments/missed-telemed/send", data);

            this.setState({
                name: "",
                chartNumber: "",
                emailAddress: "",
                appointmentTime: "",
                isLoading: false,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not send message"
            });
        }
    }

    private async lookupPatient() {
        try {
            this.setState({
                isLookingupPatient: true
            });

            const patientInfo = await V2.get<any>(`patients?chart=${this.state.chartNumber}`);

            this.setState({
                name: formatName(patientInfo.name).trim(),
                emailAddress: patientInfo.email,
                isLookingupPatient: false,
            });
        }
        catch (e) {
            if (e.statusCode === 404) {
                // Simply not found
                this.setState({
                    isLookingupPatient: false,
                    name: "",
                    emailAddress: "",
                });
                return;
            }

            this.setState({
                isLookingupPatient: false,
                isLoading: e.message || "Could not look up patient"
            });
        }
    }
}

export default withTitle("Missed Telemed Notify", withAuth(MissedTelemedsView));